import { RouterLink, useRouter } from 'vue-router'
import { ROUTES } from '@/router/routeNames'
import { funnelDefinitionEach } from '@rightmessage/flow-builder'
import { makeId, idPrefixes } from '@/services/uuid'
import iframePreview from '@/services/iframePreview'
import { getVariantDiff, hasAnswerChanged, getVariantChangeStyles, getOfferVariantDiff } from '@/modules/splittesting/services/testingUtilities'

export function useFlowBuilderAdapter(props = {}) {
  const route = useRoute()
  const router = useRouter()
  const flowBuilderStore = useFlowBuilderStore()
  const notificationStore = useNotificationStore()
  const segmentGroupStore = useSegmentGroupStore()
  const questionStore = useQuestionStore()
  const integrationStore = useIntegrationStore()
  const mediaStore = useMediaStore()
  const offerStore = useOfferStore()
  const settingsStore = useSettingsStore()
  const { globalSettings } = storeToRefs(settingsStore)
  
  // Test handlers
  const testHandlers = {
    // Handle A/B test creation
    onCreateTest: async (data) => {
      try {
        const splitTestStore = useSplitTestStore()
        
        let variants = []
        
        if (data.resourceType === 'question') {
          // Get the actual question ID from the segment group ID
          let questionId = data.resourceId
          let question = null
          if (data.segmentGroupId) {
            question = questionStore.questions.find(q => q.dimensionId === data.segmentGroupId)
            if (question) {
              questionId = question.id
            }
          }
          const segmentGroup = segmentGroupStore.getById(data.segmentGroupId)
          
          // Create question variant
          variants = [
            {
              id: makeId(idPrefixes.VARIANT),
              name: 'Variant 1',
              text: question?.text || '', // Initialize with control's text
              answers: segmentGroup?.segments?.reduce((acc, segment) => {
                // Find the option data for this segment from the question
                const option = question?.options?.find(opt => opt.segmentId === segment.id)
                
                // Get proper segment name (handles INT_OTHER)
                const segmentDisplayName = segment.name === 'INT_OTHER' && question?.otherOptions?.otherLabel
                  ? question.otherOptions.otherLabel
                  : segment.name
                
                // Create answer object with label and imageUrl
                acc[segment.id] = {
                  label: segmentDisplayName, // Initialize with proper display name
                  imageUrl: option?.imageUrl || ''
                }
                return acc
              }, {}) || {}
            }
          ]
          
          data.resourceId = questionId // Use the actual question ID (raq_*)
        } else if (data.resourceType === 'offer') {
          // Create offer variant with parts from the control offer
          const offerStore = useOfferStore()
          const offer = offerStore.getById(data.resourceId)
          
          if (offer) {
            variants = [
              {
                id: makeId(idPrefixes.VARIANT),
                name: 'Variant 1',
                parts: {
                  title: offer.parts?.title || '',
                  description: offer.parts?.description || '',
                  buttonText: offer.parts?.buttonText || '',
                  imageUrl: offer.parts?.imageUrl || ''
                }
              }
            ]
          }
        }
        
        // Create the test in draft mode
        const test = await splitTestStore.create({
          resource_type: data.resourceType,
          resource_id: data.resourceId,
          status: 'draft', // Start in draft mode
          variants: variants
        })
                
        return test
      } catch (error) {
        console.error('Failed to create A/B test:', error)
        notificationStore.addError('Failed to create A/B test')
      }
    },
    
    // Handle ending a test
    onEndTest: async (testId) => {
      try {
        const splitTestStore = useSplitTestStore()
        await splitTestStore.end(testId)
        notificationStore.addNotification('A/B test ended')
      } catch (error) {
        console.error('Failed to end A/B test:', error)
        notificationStore.addError('Failed to end A/B test')
      }
    },
    
    // Handle adding a variant
    onAddTestVariant: async (testId, variant) => {
      try {
        const splitTestStore = useSplitTestStore()
        const test = toRaw(splitTestStore.getById(testId))
        if (!test) return
        
        // Add makeId for the variant
        variant.id = makeId(idPrefixes.VARIANT)
        
        // Get the control variant (first variant) to copy structure
        const controlVariant = test.variants[0]
        if (controlVariant) {
          // Initialize text from control
          if (!variant.text && controlVariant.text) {
            variant.text = controlVariant.text
          }
          
          // Initialize answers from control if not provided
          if (!variant.answers && controlVariant.answers) {
            variant.answers = JSON.parse(JSON.stringify(controlVariant.answers))
          }
        }
        
        // Use immediate update instead of debounced for adding variants
        await splitTestStore.update(test.pid, {
          variants: [...toRaw(test.variants), variant]
        })
        
      } catch (error) {
        console.error('Failed to add variant:', error)
        notificationStore.addError('Failed to add variant')
      }
    },
    
    // Handle deleting a variant
    onDeleteTestVariant: async (testId, variantId) => {
      try {
        const splitTestStore = useSplitTestStore()
        const test = toRaw(splitTestStore.getById(testId))
        if (!test) return
        
        // Use immediate update instead of debounced for deleting variants
        await splitTestStore.update(test.pid, {
          variants: toRaw(test.variants).filter(v => v.id !== variantId)
        })
        
        notificationStore.addNotification('Removed test variant')
      } catch (error) {
        console.error('Failed to remove variant:', error)
        notificationStore.addError('Failed to remove variant')
      }
    },
    
    // Handle updating a test variant
    onUpdateTestVariant: async (testId, variantId, updates) => {
      try {
        const splitTestStore = useSplitTestStore()
        const test = splitTestStore.getById(testId)
        if (!test) return
        
        // Find and update the variant directly - it will auto-save
        const variant = test.variants.find(v => v.id === variantId)
        if (variant) {
          Object.assign(variant, updates)
        }
        
        // Don't show notification for every keystroke
      } catch (error) {
        console.error('Failed to update variant:', error)
        notificationStore.addError('Failed to update variant')
      }
    },

    // Handle viewing test results
    onViewTestResults: (testId) => {
      router.push({
        name: ROUTES.SPLIT_TESTS,
        query: { testId }
      })
    },
    
    // Handle starting a test
    onStartTest: async (testId) => {
      try {
        const splitTestStore = useSplitTestStore()
        await splitTestStore.start(testId)
        notificationStore.addNotification('Test started successfully')
      } catch (error) {
        console.error('Failed to start test:', error)
        notificationStore.addError('Failed to start test')
      }
    },
    
    // Handle deleting/canceling a test
    onDeleteTest: async (testId) => {
      try {
        const splitTestStore = useSplitTestStore()
        await splitTestStore.destroy(testId)
        notificationStore.addNotification('Test cancelled successfully')
      } catch (error) {
        console.error('Failed to delete test:', error)
        notificationStore.addError('Failed to cancel test')
      }
    }
  }
  
  const adapter = {
    // Include test handlers
    ...testHandlers,
    
    // Provide the router-link component
    linkComponent: RouterLink,
    
    // Generate link props for nodes
    getLinkProps: (node, linkId, variantId) => {
      if (!node?._uuid) return null
      
      const routeParams = {
        to: {
          name: ROUTES.OFFERFUNNELNODE,
          params: { 
            offerFunnelId: props.flow?.id,
            uuid: linkId ? linkId : node._uuid 
          }
        }
      }
      
      // Add variant query param if provided
      if (variantId) {
        routeParams.to.query = { variantId }
      }
      
      return routeParams
    },
    
    // Expose the dragging state as a computed ref for dropzones to check
    isDragging: computed(() => flowBuilderStore.isDragging),
    
    // Handle dragging state to show dropzones
    setDragging: (dragData) => {
      flowBuilderStore.isDragging = dragData
    },
    
    // Record analytics events
    recordEvent: (event, data) => {
      recordAnalyticsEvent(event, data)
    },
    
    // Show notifications
    showNotification: (message, type = 'info') => {
      if (type === 'error') {
        notificationStore.addError(message)
      } else {
        notificationStore.addNotification(message)
      }
    },
    
    // Copy to clipboard
    copyToClipboard: (text) => {
      navigator.clipboard.writeText(text).then(() => {
        notificationStore.addNotification('Copied to clipboard')
      })
    },
    
    // Generate preview URL
    generatePreviewUrl: (flow, widget) => {
      return iframePreview({ flow, widget })
    },
    
    // Create template from flow
    createTemplateFromFlow: async (flow) => {
      // Implementation for creating template from flow
      return null
    },
    
    // Handle URL navigation
    handleUrlNavigation: (url) => {
      window.open(url, '_blank')
    },
    
    // Check permissions
    hasPermission: (permission) => {
      return usePermissionStore().hasPermission(permission)
    },
    
    // Upload asset
    uploadAsset: async (file) => {
      return await mediaStore.uploadAsset(file)
    },
    
    // Get asset URL
    getAssetUrl: (path) => {
      return mediaStore.getExternalUrl(path)
    },
    
    // Check integration
    hasIntegration: (type) => {
      return integrationStore.hasIntegration(type)
    },
    
    // Validate custom field
    validateCustomField: (fieldId, partner) => {
      return partner?.fields?.some(f => f.id === fieldId) || false
    },
    
    // Node delete handler
    onNodeDelete: (node) => {
      if (!node._uuid || !props.flow) return

      const funnel = props.flow.funnelDefinition

      // Use funnelDefinitionEach to find and remove the node
      funnelDefinitionEach(funnel, (n, nuke) => {
        if (n._uuid === node._uuid) {
          // If this is a question node with an input question (raq_*), delete the question
          if (n.type === 'sg' && n.ask && n.segmentGroupId?.startsWith('raq_')) {
            const question = questionStore.getById(n.segmentGroupId)
            if (question) {
              questionStore.destroy(question)
            }
          }
          
          nuke()
          // The config store watches for changes and auto-saves
          notificationStore.addNotification('Node removed')
        }
      })
    },
    
    // Add routing to question node
    onAddRouting: (node) => {
      if (!node.ask || !node.segmentGroupId) return

      // Get the segments for this question
      const segmentGroup = segmentGroupStore.getById(node.segmentGroupId)
      if (!segmentGroup) return

      // Create split paths for each segment
      node.split = segmentGroup.segments.map(segment => ({
        segmentId: segment.id,
        child: []
      }))

      // Add a default path if the question doesn't require an answer
      const question = questionStore.getBySegmentGroupId(node.segmentGroupId)
      if (question && !question.isRequired) {
        node.split.push({
          segmentId: null,
          child: []
        })
      }

      notificationStore.addNotification('Routing added')
    },
    
    // Remove routing from question node
    onRemoveRouting: (node) => {
      if (!node.ask || !node.split) return

      // Remove all split paths
      delete node.split

      notificationStore.addNotification('Routing removed')
    },
    
    // Variant diff utilities
    getVariantDiff,
    hasAnswerChanged,
    getVariantChangeStyles,
    getOfferVariantDiff,
    
    // Global settings access
    globalSettings,

    // Page click handler - navigates to page editor
    onPageClick: (page) => {
      router.push({
        name: ROUTES.PAGE,
        params: { pageId: page.pid }
      })
    }
  }

  return adapter
}
